import { BellOff, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Notification } from '@kainos/validation';
import { cn, EmptyState, SkeletonText } from '@kainos/ui';
import { formatRelative } from '@/lib/date';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../hooks/useNotifications';

export interface NotificationTrayProps {
  onClose: () => void;
}

/**
 * The panel behind the bell.
 *
 * Every notification the workers write points at a screen, so the tray is a
 * list of destinations, not a list of texts: clicking one marks it read and
 * takes the person there. Reading is a side effect of acting, never a chore.
 */
export function NotificationTray({ onClose }: NotificationTrayProps) {
  const navigate = useNavigate();
  const { data, isPending, isError } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = data ?? [];
  const unread = notifications.filter((notification) => !notification.readAt).length;

  const open = (notification: Notification) => {
    if (!notification.readAt) markRead.mutate(notification.id);
    onClose();
    if (notification.link) navigate(notification.link);
  };

  return (
    <div
      role="dialog"
      aria-label="Notificaciones"
      className={cn(
        'absolute right-0 top-[calc(100%+10px)] z-30 flex max-h-[70vh] w-[min(380px,calc(100vw-2rem))]',
        'animate-kainos-fade-in flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-raised',
      )}
    >
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-4 py-3">
        <h2 className="text-[13.5px] font-bold text-ink">Notificaciones</h2>
        {unread > 0 ? (
          <button
            type="button"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            className="flex items-center gap-1.5 text-[12px] font-semibold text-primary transition-colors hover:text-brand disabled:opacity-50"
          >
            <CheckCheck size={13} strokeWidth={2} />
            Marcar todas como leídas
          </button>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isPending ? <SkeletonText lines={4} className="p-4" /> : null}

        {isError ? (
          <p className="px-4 py-8 text-center text-[13px] font-medium text-ink-muted">
            No pudimos cargar tus notificaciones.
          </p>
        ) : null}

        {!isPending && !isError && notifications.length === 0 ? (
          <EmptyState
            className="py-10"
            icon={<BellOff size={24} strokeWidth={1.8} className="text-ink-faint" />}
            title="Sin notificaciones"
            description="Aquí llegan los bloqueos, los riesgos detectados y los proyectos que cambian de estado."
          />
        ) : null}

        <ul>
          {notifications.map((notification) => (
            <li key={notification.id} className="border-b border-line-subtle last:border-b-0">
              <NotificationRow notification={notification} onOpen={() => open(notification)} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function NotificationRow({
  notification,
  onOpen,
}: {
  notification: Notification;
  onOpen: () => void;
}) {
  const isUnread = !notification.readAt;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex w-full gap-2.5 px-4 py-3 text-left transition-colors hover:bg-canvas',
        isUnread ? 'bg-primary-soft/60' : 'bg-surface',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full',
          isUnread ? 'bg-warning' : 'bg-transparent',
        )}
      />
      <span className="min-w-0 flex-1">
        <span className="mb-0.5 flex items-baseline justify-between gap-2">
          <span
            className={cn(
              'truncate text-[13px] text-ink',
              isUnread ? 'font-bold' : 'font-semibold',
            )}
          >
            {notification.title}
          </span>
          <span className="shrink-0 text-[11px] font-semibold text-ink-faint">
            {formatRelative(notification.createdAt)}
          </span>
        </span>
        <span className="block text-[12.5px] font-medium leading-[1.5] text-ink-body">
          {notification.body}
        </span>
        {isUnread ? <span className="sr-only">Sin leer</span> : null}
      </span>
    </button>
  );
}
